import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Gift, Copy, CheckCircle, ChartLineUp, Wallet, Link as LinkIcon, ArrowRight } from '@phosphor-icons/react';

const tiers = [
  { level: 'Bronze', referrals: '1-10', commission: '5%', color: '#CD7F32' },
  { level: 'Silver', referrals: '11-50', commission: '10%', color: '#C0C0C0' },
  { level: 'Gold', referrals: '51-100', commission: '15%', color: '#FFD700' },
  { level: 'Diamond', referrals: '100+', commission: '20%', color: '#00E5FF' },
];

const Referral = () => {
  const [walletAddress, setWalletAddress] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [isGenerated, setIsGenerated] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedField, setCopiedField] = useState('');

  const stats = {
    totalReferrals: 0,
    activeReferrals: 0,
    totalEarned: 0,
    currentTier: 'Bronze',
  };

  const generateReferralCode = () => {
    if (!walletAddress || walletAddress.length < 10) return;
    const hash = walletAddress.slice(-8).toUpperCase().replace(/[^A-Z0-9]/g, 'X');
    const code = `DEFI-${hash}`;
    setReferralCode(code);
    setReferralLink(`https://defi-bot.io?ref=${code}`);
    setIsGenerated(true);
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
    setCopied(true);
    setCopiedField(field);
    setTimeout(() => { setCopied(false); setCopiedField(''); }, 2000);
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="referral-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-medium">
            Earn Together
          </span>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter text-[var(--text-primary)] mt-4 mb-4">
            Referral Program
          </h1>
          <p className="text-lg text-[var(--text-secondary)] max-w-2xl mx-auto">
            Earn rewards by inviting others to join our advanced DeFi trading bot.
            Get generous bonuses for every active referral and build your passive income.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12"
        >
          {[
            { icon: Users, label: 'Total Referrals', value: stats.totalReferrals, color: 'var(--color-primary)' },
            { icon: CheckCircle, label: 'Active Referrals', value: stats.activeReferrals, color: 'var(--accent-success)' },
            { icon: Wallet, label: 'Total Earned', value: `${stats.totalEarned} ETH`, color: 'var(--color-primary)' },
            { icon: ChartLineUp, label: 'Current Tier', value: stats.currentTier, color: '#CD7F32' },
          ].map((stat, i) => (
            <div key={i} className="p-6 rounded-xl bg-[var(--bg-surface-1)] border border-[var(--border-default)] text-center">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: `${stat.color}15` }}>
                <stat.icon size={24} style={{ color: stat.color }} weight="bold" />
              </div>
              <p className="text-2xl font-bold text-[var(--color-primary)]" style={{ fontFamily: 'JetBrains Mono' }}>
                {stat.value}
              </p>
              <p className="text-sm text-[var(--text-secondary)] mt-1">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Your Referral Link — Wallet Connect Flow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-8 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)] mb-12"
        >
          <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-6 text-center">
            Your Referral Link
          </h3>

          <div className="max-w-2xl mx-auto space-y-4">
            {/* Wallet Input */}
            <div>
              <label className="text-sm text-[var(--text-secondary)] mb-2 block font-medium">
                Connect Your Wallet Address
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={walletAddress}
                  onChange={(e) => setWalletAddress(e.target.value)}
                  placeholder="Enter your EVM wallet address (0x...)"
                  className="flex-1 px-4 py-3 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border-default)] text-[var(--text-primary)] placeholder-[var(--text-secondary)]/50 outline-none focus:border-[var(--color-primary)] transition-colors font-mono text-sm"
                  data-testid="wallet-address-input"
                />
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={generateReferralCode}
                  disabled={!walletAddress || walletAddress.length < 10}
                  className="px-6 py-3 rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[#0047FF] text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all whitespace-nowrap"
                  data-testid="generate-link-btn"
                >
                  <LinkIcon size={18} weight="bold" />
                  Generate Link
                </motion.button>
              </div>
            </div>

            {/* Generated Referral Code & Link */}
            {isGenerated && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3 pt-2"
              >
                {/* Referral Code */}
                <div>
                  <label className="text-sm text-[var(--text-secondary)] mb-1.5 block font-medium">
                    Your Referral Code
                  </label>
                  <div className="flex items-center gap-2 p-3.5 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border-default)]">
                    <code className="flex-1 text-lg font-mono text-[var(--color-primary)] font-bold">
                      {referralCode}
                    </code>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleCopy(referralCode, 'code')}
                      className="px-4 py-2 rounded-lg bg-[var(--color-primary)]/10 text-[var(--color-primary)] text-sm font-semibold flex items-center gap-1.5 border border-[var(--color-primary)]/20 hover:bg-[var(--color-primary)]/20 transition-colors"
                      data-testid="copy-referral-code"
                    >
                      {copied && copiedField === 'code' ? <CheckCircle size={16} /> : <Copy size={16} />}
                      {copied && copiedField === 'code' ? 'Copied!' : 'Copy'}
                    </motion.button>
                  </div>
                </div>

                {/* Referral Link */}
                <div>
                  <label className="text-sm text-[var(--text-secondary)] mb-1.5 block font-medium">
                    Your Referral Link
                  </label>
                  <div className="flex items-center gap-2 p-3.5 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border-default)]">
                    <code className="flex-1 text-sm font-mono text-[var(--text-primary)] truncate">
                      {referralLink}
                    </code>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleCopy(referralLink, 'link')}
                      className="px-4 py-2 rounded-lg bg-[var(--color-primary)]/10 text-[var(--color-primary)] text-sm font-semibold flex items-center gap-1.5 border border-[var(--color-primary)]/20 hover:bg-[var(--color-primary)]/20 transition-colors"
                      data-testid="copy-referral-link"
                    >
                      {copied && copiedField === 'link' ? <CheckCircle size={16} /> : <Copy size={16} />}
                      {copied && copiedField === 'link' ? 'Copied!' : 'Copy'}
                    </motion.button>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-[var(--color-primary)]/5 border border-[var(--color-primary)]/15">
                  <p className="text-sm text-[var(--text-secondary)]">
                    <span className="text-[var(--color-primary)] font-medium">Current Tier: {stats.currentTier}</span>
                    <br />
                    You earn {stats.currentTier === 'Silver' ? '10%' : '5%'} commission on all referral purchases. Invite more users to unlock higher tiers.
                  </p>
                </div>
              </motion.div>
            )}

            {!isGenerated && (
              <div className="flex items-center gap-2 p-3.5 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border-default)]">
                <span className="text-sm text-[var(--text-secondary)]/60 font-mono">
                  Your referral link will appear here after connecting wallet
                </span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Commission Tiers & Recommended Amounts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Commission Tiers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-6 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]"
          >
            <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-6">
              Commission Tiers
            </h3>
            <div className="space-y-4">
              {tiers.map((tier) => (
                <div
                  key={tier.level}
                  className={`p-4 rounded-xl border transition-all ${
                    stats.currentTier === tier.level
                      ? 'bg-[var(--color-primary)]/10 border-[var(--color-primary)]/30'
                      : 'bg-[var(--bg-surface-2)] border-[var(--border-default)]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold"
                        style={{ backgroundColor: `${tier.color}20`, color: tier.color }}
                      >
                        {tier.level[0]}
                      </div>
                      <div>
                        <p className="font-semibold text-[var(--text-primary)]">{tier.level}</p>
                        <p className="text-sm text-[var(--text-secondary)]">{tier.referrals} referrals</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold" style={{ color: tier.color, fontFamily: 'JetBrains Mono' }}>
                        {tier.commission}
                      </p>
                      <p className="text-xs text-[var(--text-secondary)]">commission</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Recommended Start Amounts */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="p-6 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]"
          >
            <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-6">
              Recommended Start Amount
            </h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              For optimal trading performance, we recommend the following minimum starting balances per network:
            </p>
            <div className="space-y-3">
              {[
                { network: 'Ethereum', amount: '0.3 ETH', color: '#627EEA', approx: '~$720' },
                { network: 'BNB Chain', amount: '1.2 BNB', color: '#F3BA2F', approx: '~$720' },
                { network: 'Arbitrum', amount: '0.3 ETH', color: '#28A0F0', approx: '~$720' },
                { network: 'Base', amount: '0.3 ETH', color: '#0052FF', approx: '~$720' },
                { network: 'Polygon', amount: '1700 POL', color: '#8247E5', approx: '~$720' },
                { network: 'Optimism', amount: '0.3 ETH', color: '#FF0420', approx: '~$720' },
                { network: 'Avalanche', amount: '25 AVAX', color: '#E84142', approx: '~$720' },
              ].map((net) => (
                <div key={net.network} className="flex items-center justify-between p-3 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border-default)]">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${net.color}20` }}>
                      <div className="w-3 h-3 rounded-full" style={{ background: net.color }} />
                    </div>
                    <span className="font-medium text-sm text-[var(--text-primary)]">{net.network}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-sm text-[var(--color-primary)]" style={{ fontFamily: 'JetBrains Mono' }}>{net.amount}</span>
                    <span className="text-xs text-[var(--text-secondary)] ml-2">{net.approx}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* How it Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="p-8 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]"
        >
          <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-8 text-center">
            How It Works
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-[var(--color-primary)]">1</span>
              </div>
              <h4 className="font-semibold text-[var(--text-primary)] mb-2">Connect Wallet</h4>
              <p className="text-sm text-[var(--text-secondary)]">
                Enter your EVM wallet address to generate a unique referral code and link.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-[var(--color-primary)]">2</span>
              </div>
              <h4 className="font-semibold text-[var(--text-primary)] mb-2">Share Your Link</h4>
              <p className="text-sm text-[var(--text-secondary)]">
                Share your unique referral link with friends, followers, or community.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-[var(--color-primary)]">3</span>
              </div>
              <h4 className="font-semibold text-[var(--text-primary)] mb-2">Earn Rewards</h4>
              <p className="text-sm text-[var(--text-secondary)]">
                Receive commission for every purchase. Withdraw anytime to your wallet.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Referral;
